<template>
    <div class="ge-block">
        <div class="ge-header">
            <span class="ge-title">{{ team.name }}</span>
            <span class="ge-meta">{{ $tc('company.players_count', playerCount, { n: playerCount }) }}</span>
        </div>

        <home-team-dashboard :coach-id="coachId" :team-id="team.id" />

        <skeleton-loader v-if="loading" type="team-bars" />
        <rank-list v-else :title="$t('company.season_minutes')" :items="minutesItems"
            :empty-text="$t('company.no_matches_season')" />
    </div>
</template>

<script>
import { fetchStudentsAdvances } from "@/utils/Statistics";
import { getAllByIds } from "@/utils/Firebase";
import HomeTeamDashboard from "@/components/coach/HomeTeamDashboard";
import RankList from "@/components/common/RankList";
import SkeletonLoader from "@/components/common/SkeletonLoader";

const SEASON_MONTHS = 12

export default {
    name: "CoachTeamDetail",
    components: { HomeTeamDashboard, RankList, SkeletonLoader },
    props: {
        team: {
            type: Object,
            required: true,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            competences: [],
            names: {},
            loading: false,
        }
    },
    computed: {
        playerIds(vm = this) {
            const ids = []
            ;(vm.team.groups || []).forEach(group => {
                (group.students || []).forEach(id => { if (id) ids.push(id) })
            })
            return [...new Set(ids)]
        },
        playerCount(vm = this) {
            return vm.playerIds.length
        },
        minutesItems(vm = this) {
            const byStudent = {}
            vm.competences.forEach(item => {
                const id = item.studentId
                if (!id) return
                const minutes = Number(item.advance?.postInfo?.duration || 0)
                if (!minutes) return
                byStudent[id] = (byStudent[id] || 0) + minutes
            })

            const values = Object.values(byStudent)
            const max = values.length ? Math.max(...values) : 0

            return Object.keys(byStudent)
                .map(id => ({
                    id,
                    name: vm.names[id] || '',
                    value: byStudent[id],
                    max,
                    label: `${byStudent[id]}'`,
                }))
                .sort((a, b) => b.value - a.value)
        },
    },
    mounted() {
        this.loadCompetences()
    },
    methods: {
        async loadCompetences() {
            const vm = this
            if (!vm.team?.id) return
            vm.loading = true
            const end = vm.$moment().format('YYYY-MM-DD')
            const start = vm.$moment().subtract(SEASON_MONTHS, 'months').format('YYYY-MM-DD')
            try {
                vm.competences = await fetchStudentsAdvances({
                    teamId: vm.team.id,
                    start,
                    end,
                    competence: true,
                })
                await vm.loadNames()
            } catch (error) {
                console.log('loadCompetences error', error)
            } finally {
                vm.loading = false
            }
        },
        async loadNames() {
            const vm = this
            const ids = [...new Set(vm.competences.map(item => item.studentId).filter(Boolean))]
            if (ids.length === 0) return
            const docs = await getAllByIds('users', ids)
            const names = {}
            docs.forEach(doc => {
                if (!doc) return
                names[doc.id] = vm.shortName(doc.name || doc.email || '')
            })
            vm.names = names
        },
        shortName(name) {
            const parts = String(name || '').trim().split(/\s+/)
            if (parts.length < 2) return parts[0] || ''
            return `${parts[0]} ${parts[1].charAt(0).toUpperCase()}.`
        },
    },
    watch: {
        'team.id'() {
            this.loadCompetences()
        },
    },
}
</script>

<style scoped>
.ge-block {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ge-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
}

.ge-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.ge-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}
</style>
